import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PaketTestfall } from '../paket-testfall-dialog/paket-testfall-dialog.types';
import { Release } from '../../models/release.model';
import { CreatedTestRun } from '../../models/test-run.model';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { isProjectArchived, serverMessage } from '../../shared/http/server-message';
import { testRunExecutionsLink } from '../../utils/test-run-routes';
import { enthaelt, PaketEntwurf, PaketLauf, StufenOption, TestpaketeStore } from './testpakete.store';

interface PaketAnsicht {
  paket: PaketEntwurf;
  /** Nur Testfälle, die (noch) im Plan ausgewählt sind; beim Speichern fallen die anderen weg. */
  testfaelle: PaketTestfall[];
  vollstaendig: boolean;
  umgebungen: string[];
  lauf: PaketLauf | undefined;
}

@Component({
  selector: 'app-testpakete-karte',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe, NgTemplateOutlet, RouterLink, MatButton, MatIcon, MatProgressSpinner],
  templateUrl: './testpakete-karte.component.html',
  styleUrl: './testpakete-karte.component.scss',
})
export class TestpaketeKarteComponent {
  readonly testfaelle = input.required<PaketTestfall[]>();
  readonly release = input<Release | null>(null);
  /** Plan oder Testfall-Auswahl hat ungespeicherte Änderungen; dann ist „▶ Probelauf“ gesperrt. */
  readonly ungespeichert = input(false);
  readonly ohneStartrecht = input(false);
  readonly schreibgeschuetzt = input(false);
  /** Nur wer das Projekt verwaltet, darf hier eine Testumgebung anlegen; sonst fehlt „＋ weitere“. */
  readonly umgebungAnlegbar = input(false);
  /** Paket, dessen Testfälle der Dialog wählen soll; `null` legt ein neues an. */
  readonly dialogOeffnen = output<string | null>();

  protected readonly store = inject(TestpaketeStore);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly fieldLengths = FIELD_LENGTHS;
  protected readonly umgebungHinweis = signal<{ key: string; text: string } | null>(null);

  protected readonly offen = computed(() => {
    const zugeordnet = new Set(this.store.entwurf().flatMap((p) => p.testCaseIds));
    return this.testfaelle().filter((t) => !zugeordnet.has(t.id));
  });

  protected readonly stufen = computed(() => this.store.stufenOptionen(this.release()));

  protected readonly projektUmgebungen = computed(() =>
    [...this.store.umgebungen().eintraege].sort((a, b) => a.position - b.position).map((u) => u.name),
  );

  protected readonly pakete = computed<PaketAnsicht[]>(() => {
    const nachId = new Map(this.testfaelle().map((t) => [t.id, t]));
    const laeufe = this.store.laeufe();
    return this.store.entwurf().map((paket) => {
      const testfaelle = paket.testCaseIds.flatMap((id) => nachId.get(id) ?? []);
      return {
        paket,
        testfaelle,
        vollstaendig: this.store.vollstaendig({ ...paket, testCaseIds: testfaelle.map((t) => t.id) }),
        umgebungen: this.umgebungenFuer(paket),
        lauf: laeufe[paket.key],
      };
    });
  });

  /** „Testläufe beim Planen“ = Summe der Umgebungen aller Pakete (P17). */
  protected readonly kennzahlen = computed(() => {
    const pakete = this.pakete();
    return {
      pakete: pakete.length,
      testfaelle: pakete.reduce((n, p) => n + p.testfaelle.length, 0),
      unvollstaendig: pakete.filter((p) => !p.vollstaendig).length,
      laeufe: pakete.reduce((n, p) => n + p.paket.environments.length, 0),
    };
  });

  /** Filter bieten nur Werte an, die in den Paketen vorkommen (P18). */
  protected readonly filterStufen = computed<StufenOption[]>(() => {
    const namen = new Map(this.stufen().map((s) => [s.id, s.name]));
    const ids = [...new Set(this.store.entwurf().flatMap((p) => p.projectTestStageId ?? []))];
    return ids.map((id) => ({ id, name: namen.get(id) ?? id }));
  });

  protected readonly filterUmgebungen = computed(() => [
    ...new Set(this.store.entwurf().flatMap((p) => p.environments)),
  ]);

  protected readonly gefiltert = computed<PaketAnsicht[]>(() => {
    const nachKey = new Map(this.pakete().map((p) => [p.paket.key, p]));
    return this.store.gefiltert(this.testfaelle()).flatMap((p) => nachKey.get(p.key) ?? []);
  });

  protected readonly sichtbar = computed(() => this.gefiltert().slice(0, this.store.anzahl()));

  protected readonly gefilterteKeys = computed(() => this.gefiltert().map((p) => p.paket.key));

  protected readonly alleMarkiert = computed(() => {
    const markiert = this.store.markiert();
    const keys = this.gefilterteKeys();
    return keys.length > 0 && keys.every((k) => markiert.includes(k));
  });

  protected oeffneDialog(paketKey: string | null): void {
    this.store.oeffneDialog(paketKey);
    this.dialogOeffnen.emit(paketKey);
  }

  protected schalteOffen(key: string): void {
    this.leereHinweisDesOffenenPakets();
    this.store.schalteOffen(key);
  }

  protected oeffne(key: string): void {
    if (key !== this.store.offenKey()) {
      this.leereHinweisDesOffenenPakets();
    }
    this.store.oeffne(key);
  }

  /** Das Feld verliert beim Schließen seinen Text. Ein später Fehler eines zugeklappten Pakets bleibt stehen. */
  private leereHinweisDesOffenenPakets(): void {
    if (this.umgebungHinweis()?.key === this.store.offenKey()) {
      this.umgebungHinweis.set(null);
    }
  }

  protected stufenName(paket: PaketEntwurf): string {
    const stufe = this.stufen().find((s) => s.id === paket.projectTestStageId);
    return stufe?.name ?? this.translate.instant('testPlans.packages.noStage');
  }

  protected laufLink(lauf: CreatedTestRun): string[] {
    return testRunExecutionsLink(this.store.projektId(), lauf.id).commands;
  }

  protected laufListe(eintrag: PaketAnsicht): CreatedTestRun[] {
    return eintrag.lauf?.status === 'fertig' ? eintrag.lauf.laeufe : [];
  }

  /** Ein Probelauf startet das gespeicherte Paket; bei offenen Änderungen erst speichern. */
  protected startGesperrt(paket: PaketEntwurf): boolean {
    return this.ungespeichert() || this.store.geaendert() || !paket.id;
  }

  protected starteProbelauf(key: string): void {
    this.store.probelauf(key).subscribe({
      error: (err: unknown) => {
        if (isProjectArchived(err)) {
          return;
        }
        const text = probelaufFehlerText(err) ?? this.translate.instant('testPlans.packages.trialError');
        this.snackBar.open(text, undefined, { duration: 5000 });
      },
    });
  }

  protected sammelStufe(event: Event): void {
    const auswahl = event.target as HTMLSelectElement;
    if (auswahl.value) {
      this.store.setzeStufeFuerMarkierte(auswahl.value);
    }
    auswahl.value = '';
  }

  protected sammelUmgebung(event: Event): void {
    const auswahl = event.target as HTMLSelectElement;
    if (auswahl.value) {
      this.store.fuegeUmgebungZuMarkierten(auswahl.value);
    }
    auswahl.value = '';
  }

  protected gewaehlt(paket: PaketEntwurf, umgebung: string): boolean {
    return enthaelt(paket.environments, umgebung);
  }

  protected legeUmgebungAn(paket: PaketEntwurf, event: Event): void {
    event.preventDefault();
    const feld = event.target as HTMLInputElement;
    const name = feld.value.trim();
    if (!name) {
      return;
    }
    this.umgebungHinweis.set(null);
    this.store
      .legeUmgebungAn(paket.key, name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          feld.value = '';
          this.fokussiere(feld);
        },
        error: (err: unknown) => {
          this.umgebungFehler(paket.key, err);
          this.fokussiere(feld);
        },
      });
  }

  /** Das gesperrte Feld verliert den Fokus; wer per Tastatur anlegt, tippt gleich weiter. */
  private fokussiere(feld: HTMLInputElement): void {
    afterNextRender(
      () => {
        const aktiv = feld.ownerDocument.activeElement;
        if (aktiv === feld || aktiv === feld.ownerDocument.body) {
          feld.focus();
        }
      },
      { injector: this.injector },
    );
  }

  private umgebungFehler(key: string, err: unknown): void {
    if (isProjectArchived(err)) {
      return;
    }
    const text = serverMessage(err);
    if (text) {
      this.umgebungHinweis.set({ key, text });
    } else {
      this.snackBar.open(this.translate.instant('testPlans.packages.environmentCreateError'), undefined, {
        duration: 5000,
      });
    }
  }

  protected umbenennen(paket: PaketEntwurf, event: Event): void {
    this.store.aendere(paket.key, { name: this.wert(event) });
  }

  protected waehleStufe(paket: PaketEntwurf, event: Event): void {
    this.store.waehleStufe(paket.key, this.wert(event) || null, this.release());
  }

  protected entferneTestfall(paket: PaketEntwurf, testfallId: string): void {
    this.store.aendere(paket.key, { testCaseIds: paket.testCaseIds.filter((id) => id !== testfallId) });
  }

  protected wert(event: Event): string {
    return (event.target as HTMLInputElement | HTMLSelectElement).value;
  }

  /** Umgebungen des Projekts, dazu gespeicherte, die es im Projekt nicht (mehr) gibt. */
  private umgebungenFuer(paket: PaketEntwurf): string[] {
    const projekt = this.projektUmgebungen();
    return [...projekt, ...paket.environments.filter((u) => !enthaelt(projekt, u))];
  }
}

/** P22: Auch ein 403 (etwa `PROJECT_ARCHIVED`) bringt den Grund der API mit; `serverMessage` liest nur 400/409. */
function probelaufFehlerText(err: unknown): string | null {
  if (err instanceof HttpErrorResponse && err.status === 403) {
    const text: unknown = (err.error as { error?: unknown } | null)?.error;
    return typeof text === 'string' && text.length > 0 ? text : null;
  }
  return serverMessage(err);
}
